import * as stylex from "@stylexjs/stylex";

import TechBadge from "../badge/TechBadge";
import { WORK_EXPERIENCE } from "../data/placeholder";

function Work() {
  return (
    <div {...stylex.props(styles.container)}>
      <div {...stylex.props(styles.header)}>
        <h1 {...stylex.props(styles.title, styles.titleGradient)}>
          Work Experience
        </h1>
        <p {...stylex.props(styles.subtitle)}>
          A timeline of my professional journey and key achievements
        </p>
      </div>

      <div {...stylex.props(styles.timeline)}>
        {WORK_EXPERIENCE.map((job, index) => (
          <div
            key={index}
            {...stylex.props(styles.timelineItem, {
              animationDelay: `${index * 0.15}s`,
            })}
          >
            <div {...stylex.props(styles.timelineDot)} />
            <div {...stylex.props(styles.card)}>
              <div {...stylex.props(styles.cardHeader)}>
                <div>
                  <h2 {...stylex.props(styles.role)}>{job.role}</h2>
                  <h3 {...stylex.props(styles.company)}>{job.company}</h3>
                </div>
                <span {...stylex.props(styles.period)}>{job.period}</span>
              </div>
              <p {...stylex.props(styles.description)}>{job.description}</p>
              {job.tech && (
                <div {...stylex.props(styles.techStack)}>
                  {job.tech.map((tech, i) => (
                    <TechBadge key={i}>{tech}</TechBadge>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Work;

const fadeIn = stylex.keyframes({
  "0%": { opacity: 0, transform: "translateY(20px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const styles = stylex.create({
  container: {
    gap: "3rem",
    marginInline: "auto",
    backgroundAttachment: "fixed",
    backgroundImage: "var(--bg-gradient)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    maxWidth: "1200px",
    minHeight: "100vh",
    paddingBottom: "4rem",
    paddingLeft: "2rem",
    paddingRight: "2rem",
    paddingTop: "4rem",
    width: "100%",
  },
  header: {
    animationDuration: "0.8s",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    textAlign: "center",
    marginBottom: "2rem",
  },
  title: {
    color: "var(--primary-text)",
    display: "block",
    fontSize: {
      default: "3.5rem",
      "@media (max-width: 768px)": "2.25rem",
    },
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1.3,
    position: "relative",
    marginBottom: "1rem",
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  titleGradient: {
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundImage: "var(--primary-gradient)",
  },
  subtitle: {
    color: "var(--text-secondary)",
    fontSize: "1.2rem",
    lineHeight: 1.5,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  timeline: {
    gap: "3rem",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    paddingLeft: "2rem",
    "::before": {
      borderRadius: "4px",
      backgroundImage: "var(--timeline-line-bg)",
      boxShadow: "var(--timeline-dot-shadow)",
      content: "''",
      position: "absolute",
      bottom: "0",
      left: "0",
      top: "0",
      width: "4px",
    },
  },
  timelineItem: {
    animationDuration: "0.6s",
    animationFillMode: "both",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    position: "relative",
  },
  timelineDot: {
    borderColor: "var(--primary-accent)",
    borderRadius: "50%",
    borderStyle: "solid",
    borderWidth: "4px",
    transition: "transform 0.3s ease",
    backgroundColor: "var(--nav-bar-background)",
    boxShadow: "var(--timeline-dot-shadow)",
    position: "absolute",
    height: "1.2rem",
    left: "-2.7rem",
    top: "2.5rem",
    width: "1.2rem",
  },
  card: {
    borderColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--glow-border)",
    },
    borderRadius: "var(--card-corner-radius)",
    borderStyle: "solid",
    borderWidth: "1px",
    gap: "1.5rem",
    transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    backdropFilter: "blur(20px)",
    backgroundColor: "var(--surface-glass)",
    boxShadow: {
      default: "var(--shadow-sm)",
      ":hover": "var(--shadow-neon)",
    },
    display: "flex",
    flexDirection: "column",
    transform: {
      default: null,
      ":hover": "translateY(-4px) scale(1.01)",
    },
    paddingBottom: "2.5rem",
    paddingLeft: "2.5rem",
    paddingRight: "2.5rem",
    paddingTop: "2.5rem",
  },
  cardHeader: {
    gap: "1rem",
    alignItems: "flex-start",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  role: {
    color: "var(--primary-text)",
    fontSize: "1.8rem",
    fontWeight: 700,
    marginBottom: "0.25rem",
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  company: {
    color: "var(--primary-accent)",
    fontSize: "1.2rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  period: {
    borderColor: "transparent",
    borderRadius: "100px",
    borderStyle: "solid",
    borderWidth: "1px",
    backgroundColor: "var(--wash)",
    color: "var(--primary-text)",
    fontSize: "0.9rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    paddingBottom: "0.5rem",
    paddingLeft: "1.2rem",
    paddingRight: "1.2rem",
    paddingTop: "0.5rem",
  },
  description: {
    color: "var(--text-secondary)",
    fontSize: "1.1rem",
    lineHeight: 1.7,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  techStack: {
    gap: "0.75rem",
    display: "flex",
    flexWrap: "wrap",
    marginTop: "1rem",
  },
});
